// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { xScreen } from "@werner/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    buttonTitle = "buttonTitle",
    buttonHref = "buttonHref",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonTitle]: string;
        [fields.buttonHref]: LinkFieldValue;
    };
}

export interface TrainingHeaderStyles {
    wrapper?: InjectableCss;
    buttonStyles?: ButtonPresentationProps;
    pageTitle?: TypographyProps;
    content: InjectableCss;
}
export const TrainingHeaderStyles: TrainingHeaderStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                row-gap: 20px;
                padding: 0;
            }
        `,
    },
    buttonStyles: {
        variant: "primary",
        typographyProps: {
            size: 16,
            lineHeight: "16px",
            weight: "normal",
            transform: "capitalize",
        },
    },
    content: {
        css: css`
            line-height: 22px;
            size: 18px;
            text-align: left;
            ${xScreen("lte", "sm")} {
                display: block;
                max-width: 100%;
            }
        `,
    },
    pageTitle: {
        variant: "h2",
        as: "h1",
        ellipsis: true,
        css: css`
            margin-bottom: 40px;
            line-height: 32px;
            size: 40px;
            ${xScreen("lte", "xs")} {
                &&& {
                    size: 28px;
                    padding: 0;
                }
            }
        `,
    },
};

const styles = TrainingHeaderStyles;

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps>;

const TrainingCalenderHeader: React.FunctionComponent<Props> = ({ pageTitle, id, fields }) => {
    const history = useHistory();
    const link = useGetLink(fields.buttonHref);
    const { isInShell } = useShellContext();

    const onClickHandler = () => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
    };

    return (
        <>
            <Typography {...styles.pageTitle}>{pageTitle}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.content}>
                    <XngZone contentId={id} zoneName="ContentMainHeaderDescription00" hint="Description" normalWidth />
                </StyledWrapper>
                <Button {...styles.buttonStyles} type="button" onClick={onClickHandler} aria-label={`${fields.buttonTitle}`}>
                    {fields.buttonTitle}
                </Button>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TrainingCalenderHeader),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.buttonTitle,
                displayName: "Button Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.buttonHref,
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Url",
                    value: "",
                },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
